import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ContractModel } from '../../../../../../../core/models/contract.model';
import { ENUM_PERMISSIONS } from '../../../../../../../core/enums/permission.enum';
import { enableControlWhenAllowed, lockFormWhenReadOnly } from 'src/app/core/utils/form-permission.util';
import { ContractTypeEnum, ContractValidityEnum } from '../../../../../../../core/enums/contract.enum';
import { ContractStatusEnum } from '../../../../../../../core/enums/contract-status.enum';
import { GenericEngagementFormatEnum } from '../../../../../../../core/enums/generic-engagement-format.enum';
import { TimeUnit, translateTimeUnitObject } from '../../../../../../../core/utils/time-utils';
import { Observable, Subscription, tap } from 'rxjs';
import { GenericEngagementModel } from '../../../../../../../core/models/generic-engagement.model';
import { UserProfileModel } from '../../../../../../../core/models/user-profile.model';
import { SupplierModel } from '../../../../../../../core/models/supplier.model';
import { SupplierContactModel } from '../../../../../../../core/models/supplier-contact.model';
import moment from 'moment';
import { GenericPaymentConditionModel } from '../../../../../../../core/models/generic-payment-condition.model';
import { scaleIn400ms } from '../../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../../@vex/animations/scale-fade-in.animation';
import { BaseContactType, ContactTypeEnum } from '../../../../../../../core/enums/contact-type.enum';
import { SnackbarService } from '../../../../../../../core/services/snackbar.service';
import { UserProfileService } from '../../../../user-profile/user-profile.service';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, skip, startWith, take } from 'rxjs/operators';
import { ContractService } from '../../../contract.service';
import { SupplierService } from '../../../../supplier/supplier.service';
import { GenericPaymentConditionService } from '../../../../../settings/generic-payment-condition/generic-payment-condition.service';
import { ActivatedRoute } from '@angular/router';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { ContractResumeCreateUpdateDeleteComponent } from '../../../modals/contract-resume-create-update-delete/contract-resume-create-update-delete.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SupplierContactCreateUpdateDeleteComponent } from '../../../../supplier/modals/supplier-contact-create-update-delete/supplier-contact-create-update-delete.component';
import { CrudModeEnum } from '../../../../../../../core/base/enum/crud-mode.enum';

@Component({
  selector: 'vex-contract-supplier-details-resume',
  templateUrl: './contract-supplier-details-resume.component.html',
  styleUrls: ['./contract-supplier-details-resume.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
  encapsulation: ViewEncapsulation.None,
})
export class ContractSupplierDetailsResumeComponent implements OnInit, OnDestroy, OnChanges {
  @Input() contract: ContractModel;
  @Input() files: any[];
  @Input() isUpdateDisabledPermission: boolean;
  @Input() genericEngagement$: Observable<GenericEngagementModel[]>;
  @Input() engagementContacts$: Observable<UserProfileModel[]>;
  @Input() suppliers$: Observable<SupplierModel[]>;
  @Input() supplierContacts$: Observable<SupplierContactModel[]>;
  @Input() genericPaymentCondition$: Observable<GenericPaymentConditionModel[]>;

  @Output() updateStep = new EventEmitter<number>();
  @Output() switchToUpdateMode = new EventEmitter<void>();
  @Output() genericEngagementAutoCompleteChange = new EventEmitter<any>();
  @Output() engagementContactsAutoCompleteChange = new EventEmitter<any>();
  @Output() supplierAutocompleteChange = new EventEmitter<any>();
  @Output() addSupplier = new EventEmitter<any>();
  @Output() downloadDocument = new EventEmitter<any>();
  @Output() fileDropped = new EventEmitter<any>();

  form: UntypedFormGroup;
  ContractValidityEnum = ContractValidityEnum;
  ContractStatusEnum = ContractStatusEnum;
  genericEngagementFormatEnum = GenericEngagementFormatEnum;
  ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  hasGenericEngagement: boolean;
  TimeUnit = TimeUnit;
  public minNbDay = 0;
  public maxNbDay = 10;
  public loading = false;
  public loadingSave = false;
  public loadingFile = false;
  private subscription = new Subscription();
  private isCalculatingRenewal = false;
  public defaultValidity = ContractValidityEnum.FIRM;
  public contractValidityEnum = Object.values(ContractValidityEnum);
  public signatureCardTouched = false;

  constructor(
    private snackBarService: SnackbarService,
    private userProfileService: UserProfileService,
    public contractService: ContractService,
    private supplierService: SupplierService,
    private genericPaymentConditionService: GenericPaymentConditionService,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.hasGenericEngagement =
      this.contract?.genericEngagement !== null && this.contract?.genericEngagement !== undefined;

    // 1. Initialiser le formulaire (sans souscriptions valueChanges)
    this.initForm();

    // 2. Configurer les souscriptions valueChanges APRÈS l'initialisation
    this.setupValueChangesSubscriptions();

    // 3. Configurer la souscription pour auto-save (uniquement en mode création)
    if (this.contract?.creationStep === 1) {
      this.initValueChangesSubscription();
    }
    // PRED-3230 : lecture seule intégrale sans droit de modification.
    lockFormWhenReadOnly(this.form, this.isUpdateDisabledPermission);
  }

  getNoticePeriodLabel(): string {
    const noticePeriod = this.form.controls.noticePeriod.value;
    if (!noticePeriod || !noticePeriod.displayedUnit) {
      return 'Délai de prévenance';
    }

    try {
      const unitLabel = translateTimeUnitObject(noticePeriod.displayedUnit).label;
      return `Délai de prévenance (${unitLabel})`;
    } catch (error) {
      console.warn('Unsupported time unit:', noticePeriod.displayedUnit, error);
      return 'Délai de prévenance';
    }
  }

  signatureDateFilter = (d: Date | null): boolean => {
    const endValidityDate = this.form.controls.endValidityDate.value;
    return d <= moment(endValidityDate).toDate();
  };

  public supplierContactsAutocompleteChange(state: any = '') {
    this.supplierContacts$ = this.supplierService
      .findAllSupplierContactById(null, null, null, null, '', {
        supplierId: this.form.controls.supplier.value?._id,
        type: BaseContactType.find(x => x.label === this.contract.genericEngagement?.format)?._id,
        quoteId: this.contract?.quoteId,
        establishmentId: this.contract?.quote?.establishment._id,
      })
      .pipe(map(x => x.data.map(y => ({ ...y, fullName: `${y.firstName} ${y.lastName}` }))));
  }

  private initForm() {
    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(),
      genericEngagement: new UntypedFormControl(null, Validators.required),
      object: new UntypedFormControl(null, Validators.required),
      supplier: new UntypedFormControl(null, Validators.required),
      supplierContractReference: new UntypedFormControl(null, Validators.required),
      supplierContact: new UntypedFormControl(null, Validators.required),
      validity: new UntypedFormControl(null, Validators.required),
      startValidityDate: new UntypedFormControl(null, Validators.required),
      endValidityDate: new UntypedFormControl({ value: null, disabled: true }, Validators.required),
      signatureDate: new UntypedFormControl({ value: null, disabled: true }, Validators.required),
      renewalValidity: new UntypedFormControl({ value: null, disabled: true }, [
        Validators.required,
        Validators.min(0),
      ]),
      minNotice: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.min(0)]),
      paymentCondition: new UntypedFormControl(null, Validators.required),
      nbDay: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.min(0)]),
      renewalNb: new UntypedFormControl({ value: null, disabled: true }, [Validators.min(0)]),
      renewalDate: new UntypedFormControl({ value: null, disabled: true }),
      noticePeriod: new UntypedFormControl({ value: { displayedUnit: TimeUnit.DAY, quantity: 10080 } }),
      engagementContact: new UntypedFormControl(null),
    });

    this.form.patchValue(
      {
        _id: this.contract._id,
        genericEngagement: this.contract.genericEngagement,
        object: this.contract.object,
        supplier: this.contract.supplier,
        supplierContractReference: this.contract.supplierContractReference,
        supplierContact: this.contract.supplierContact,
        validity: this.contract.validity,
        startValidityDate: this.contract.startValidityDate,
        endValidityDate: this.contract.endValidityDate,
        signatureDate: this.contract.signatureDate,
        renewalValidity: this.contract.renewalValidity,
        minNotice: this.contract.minNotice,
        paymentCondition: this.contract.paymentCondition,
        nbDay: this.contract.nbDay,
        renewalNb: this.contract.renewalNb,
        renewalDate: this.contract.renewalDate,
        noticePeriod: this.contract.noticePeriod
          ? this.contract.noticePeriod
          : {
              displayedUnit: TimeUnit.DAY,
              quantity: 10080,
            },
        engagementContact: this.contract.engagementContact,
        supportEstablishment: null,
      },
      { emitEvent: false },
    );

    if (this.contract.paymentCondition) {
      const paymentCondition = this.contract.paymentCondition;
      this.contract.paymentCondition.displayProperty = paymentCondition.nbDay
        ? `${paymentCondition.label} (Maximum ${paymentCondition.nbDay} jours)`
        : `${paymentCondition.label}`;
      this.maxNbDay = paymentCondition.nbDay;
      if (this.contract.creationStep !== 1) {
        if (paymentCondition.nbDay) {
          this.contract.paymentCondition.displayProperty = paymentCondition.label.replace(
            'X',
            this.contract.nbDay.toString(),
          );
        }
      }
    }

    if (this.contract.validity) {
      this.defaultValidity = this.contract.validity;
    }

    if (this.contract.startValidityDate) {
      enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);
    }

    if (this.contract.endValidityDate) {
      enableControlWhenAllowed(this.form, 'signatureDate', this.isUpdateDisabledPermission);
    }

    if (this.contract.validity === ContractValidityEnum.DENIABLE) {
      enableControlWhenAllowed(this.form, 'minNotice', this.isUpdateDisabledPermission);
      enableControlWhenAllowed(this.form, 'renewalValidity', this.isUpdateDisabledPermission);
      enableControlWhenAllowed(this.form, 'renewalNb', this.isUpdateDisabledPermission);
      enableControlWhenAllowed(this.form, 'renewalDate', this.isUpdateDisabledPermission);
    }

    if (this.contract.paymentCondition && this.contract.paymentCondition.nbDay) {
      enableControlWhenAllowed(this.form, 'nbDay', this.isUpdateDisabledPermission);
    } else {
      this.form.controls.nbDay.disable({ emitEvent: false });
    }

    if (this.contract.genericEngagement?.format === GenericEngagementFormatEnum.QUOTE) {
      this.form.controls.validity.setValue(ContractValidityEnum.FIRM, { emitEvent: false });
      this.form.controls.validity.disable({ emitEvent: false });
    } else {
      if (!this.isUpdateDisabledPermission) {
        enableControlWhenAllowed(this.form, 'validity', this.isUpdateDisabledPermission);
      }
    }

    if (this.contract.genericEngagement) {
      enableControlWhenAllowed(this.form, 'engagementContact', this.isUpdateDisabledPermission);
    } else {
      this.form.controls.engagementContact.disable({ emitEvent: false });
    }

    // Vérifier la validité des dates localement sans appel API
    this.checkValidityDatesLocal();
  }

  private setupValueChangesSubscriptions(): void {
    this.subscription.add(
      this.form.controls.validity.valueChanges.subscribe((validity: ContractValidityEnum) => {
        if (validity && validity === ContractValidityEnum.DENIABLE) {
          enableControlWhenAllowed(this.form, 'minNotice', this.isUpdateDisabledPermission);
          enableControlWhenAllowed(this.form, 'renewalValidity', this.isUpdateDisabledPermission);
          this.form.controls.minNotice.setValidators([Validators.required, Validators.min(0)]);
          this.form.controls.renewalValidity.setValidators([Validators.required, Validators.min(0)]);

          enableControlWhenAllowed(this.form, 'renewalNb', this.isUpdateDisabledPermission);
          enableControlWhenAllowed(this.form, 'renewalDate', this.isUpdateDisabledPermission);
          this.form.controls.renewalNb.setValidators([Validators.min(0)]);
          this.defaultValidity = ContractValidityEnum.DENIABLE;
        } else {
          this.form.controls.minNotice.setValue(null, { emitEvent: false });
          this.form.controls.renewalValidity.setValue(null, { emitEvent: false });
          this.form.controls.minNotice.disable({ emitEvent: false });
          this.form.controls.renewalValidity.disable({ emitEvent: false });
          this.form.controls.minNotice.setValidators(null);
          this.form.controls.renewalValidity.setValidators(null);

          this.form.controls.renewalNb.setValue(null, { emitEvent: false });
          this.form.controls.renewalDate.setValue(null, { emitEvent: false });
          this.form.controls.renewalNb.disable({ emitEvent: false });
          this.form.controls.renewalDate.disable({ emitEvent: false });
          this.form.controls.renewalNb.setValidators(null);
          this.form.controls.renewalDate.setValidators(null);
          this.defaultValidity = ContractValidityEnum.FIRM;
        }
      }),
    );

    this.subscription.add(
      this.form.controls.supplier.valueChanges.subscribe(async (supplier: SupplierModel) => {
        if (supplier) {
          try {
            const result = await this.supplierContactAutoCompleteChange(null, supplier._id);

            if (result.length > 0) {
              this.form.controls.supplierContact.setValue(result[0], { emitEvent: false });
            }
          } catch (error) {
            console.error('Error fetching supplier contacts:', error);
          }
        } else {
          this.form.controls.supplierContact.setValue(null, { emitEvent: false });
        }
      }),
    );

    this.subscription.add(
      this.form.controls.startValidityDate.valueChanges.subscribe((date: Date) => {
        if (date) {
          enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);
        } else {
          this.form.controls.endValidityDate.setValue(null, { emitEvent: false });
          this.form.controls.endValidityDate.disable();
        }
      }),
    );

    this.subscription.add(
      this.form.controls.endValidityDate.valueChanges.subscribe((date: Date) => {
        const startValidityDate = this.form.controls.startValidityDate.value;
        const startDate = moment(startValidityDate).startOf('day').toDate();
        const endDate = moment(date).startOf('day').toDate();

        if (date) {
          if (endDate >= startDate) {
            if (!this.isUpdateDisabledPermission) {
              enableControlWhenAllowed(this.form, 'signatureDate', this.isUpdateDisabledPermission);
            }
            this.resultRenewalNbField();
            this.resultRenewalNbFromRenewalDate();
          } else {
            this.form.controls.signatureDate.setValue(null, { emitEvent: false });
            this.form.controls.signatureDate.disable({ emitEvent: false });
          }
        } else {
          this.form.controls.signatureDate.setValue(null, { emitEvent: false });
          this.form.controls.signatureDate.disable({ emitEvent: false });
        }
      }),
    );

    this.subscription.add(
      this.form.controls.renewalValidity.valueChanges.subscribe(() => {
        this.resultRenewalNbField();
        this.resultRenewalNbFromRenewalDate();
      }),
    );

    this.subscription.add(
      this.form.controls.renewalNb.valueChanges.subscribe(() => {
        this.resultRenewalNbField();
      }),
    );

    this.subscription.add(
      this.form.controls.renewalDate.valueChanges.subscribe(() => {
        this.resultRenewalNbFromRenewalDate();
        this.resultRenewalNbField();
      }),
    );

    this.subscription.add(
      this.form.controls.paymentCondition.valueChanges.subscribe((paymentCondition: GenericPaymentConditionModel) => {
        if (paymentCondition) {
          this.maxNbDay = paymentCondition.nbDay;
          if (paymentCondition.nbDay) {
            enableControlWhenAllowed(this.form, 'nbDay', this.isUpdateDisabledPermission);
          } else {
            this.form.controls.nbDay.disable({ emitEvent: false });
          }
        } else {
          this.form.controls.nbDay.disable({ emitEvent: false });
        }
        this.form.controls.nbDay.setValue(null, { emitEvent: false });
      }),
    );

    this.subscription.add(
      this.form.controls.nbDay.valueChanges.subscribe((nbDay: number) => {
        if (nbDay && nbDay > this.maxNbDay) {
          this.form.controls.nbDay.setValue(this.maxNbDay, { emitEvent: false });
          this.form.controls.nbDay.setErrors({ max: true });
        }
      }),
    );

    this.subscription.add(
      this.form.controls.genericEngagement.valueChanges.subscribe(value => {
        this.engagementContacts$ = this.userProfileService
          .findAll(null, null, null, null, '', {
            isEngagementContacts: value?.engagementCategory?._id,
          })
          .pipe(
            map(userProfile => {
              return userProfile.data.map(x => x);
            }),

            tap(userProfile => {
              if (!userProfile.find(x => x._id === this.form.controls.engagementContact.value?._id)) {
                this.form.controls.engagementContact.setValue(null, { emitEvent: false });
                enableControlWhenAllowed(this.form, 'engagementContact', this.isUpdateDisabledPermission);
              }
            }),
          );

        if (value.format === ContractTypeEnum.QUOTE) {
          this.form.controls.validity.setValue(ContractValidityEnum.FIRM, { emitEvent: false });
          this.form.controls.validity.disable({ emitEvent: false });
          this.defaultValidity = ContractValidityEnum.FIRM;
        } else {
          enableControlWhenAllowed(this.form, 'validity', this.isUpdateDisabledPermission);
        }

        if (
          (value.format === ContractTypeEnum.QUOTE &&
            !this.form.controls.supplierContact.value?.contactTypes
              .map(x => x.label)
              .includes(ContactTypeEnum.DEVIS)) ||
          (value.format === ContractTypeEnum.CONTRACT &&
            !this.form.controls.supplierContact.value?.contactTypes
              .map(x => x.label)
              .includes(ContactTypeEnum.CONTRACT))
        ) {
          this.form.controls.supplierContact.setValue(null, { emitEvent: false });
        }

        return new Promise((resolve, reject) => {
          this.supplierContacts$ = this.supplierService
            .findAllSupplierContactById(null, null, null, null, '', {
              supplierId: this.form.controls.supplier.value?._id,
              type: BaseContactType.find(x => x.label === value.format)?._id,
            })
            .pipe(map(x => x.data.map(y => ({ ...y, fullName: `${y.firstName} ${y.lastName}` }))));

          this.supplierContacts$.subscribe(
            data => {
              resolve(data);
            },
            error => {
              reject(error);
            },
          );
        });
      }),
    );
  }

  // à revoir peut etre

  public async supplierContactAutoCompleteChange(state: any = '', supplierId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const sub = this.contractService.entity$.pipe(take(1)).subscribe((contract: ContractModel) => {
        const contractGenericEngagementStatus = contract.genericEngagement?.format;

        this.supplierContacts$ = this.supplierService
          .findAllSupplierContactById(null, null, null, null, state, {
            supplierId,
            type: BaseContactType.find(x => x.label === contractGenericEngagementStatus)?._id,
          })
          .pipe(
            map(x => {
              return x.data.map(y => ({ ...y, fullName: `${y.firstName} ${y.lastName}` }));
            }),
          );

        this.supplierContacts$.subscribe(
          data => {
            resolve(data);
          },
          error => {
            reject(error);
          },
        );
      });
      this.subscription.add(sub);
    });
  }

  resultRenewalNbFromRenewalDate() {
    if (!this.isCalculatingRenewal) {
      this.isCalculatingRenewal = true;
      const endValidityDate = moment(this.form.controls.endValidityDate.value);
      const renewalDate = moment(this.form.controls.renewalDate.value);
      const renewalValidity = this.form.controls.renewalValidity.value;
      const renewalNbField = this.form.controls.renewalNb.value;

      if (
        endValidityDate.isValid() &&
        renewalDate.isValid() &&
        renewalValidity !== null &&
        renewalNbField !== undefined &&
        renewalNbField !== null &&
        renewalValidity !== undefined
      ) {
        const monthsDiff = renewalDate.diff(endValidityDate, 'months');
        let renewalNb = monthsDiff / renewalValidity;

        // Pour l'instant, je réalise des arrondis car pas de décimale demandée (carte 1313) : Rob, le 12/01/2024
        // Afficher 0 si la valeur est inférieure à 0.5, pour l'instant
        renewalNb = renewalNb < 0.5 ? 0 : renewalNb % 1 >= 0.5 ? Math.ceil(renewalNb) : Math.floor(renewalNb);

        this.form.controls.renewalNb.setValue(renewalNb);
      }
      this.isCalculatingRenewal = false;
    }
  }

  resultRenewalNbField() {
    if (!this.isCalculatingRenewal) {
      this.isCalculatingRenewal = true;
      const endValidityDate = this.form.controls.endValidityDate.value;
      const renewalValidity = this.form.controls.renewalValidity.value;
      const renewalNb = this.form.controls.renewalNb.value;
      if (
        endValidityDate &&
        renewalValidity !== null &&
        renewalNb !== null &&
        renewalValidity !== undefined &&
        renewalNb !== undefined
      ) {
        const renewalDate = moment(endValidityDate)
          .add(renewalValidity * renewalNb, 'months')
          .toDate();
        this.form.controls.renewalDate.setValue(renewalDate);
      }
      this.isCalculatingRenewal = false;
    }
  }

  public genericPaymentConditionAutoCompleteChange(state: any = '') {
    this.genericPaymentCondition$ = this.genericPaymentConditionService.findAll(null, null, null, null, state).pipe(
      map(genericPaymentCondition => {
        return genericPaymentCondition.data.map(x => {
          if (x.nbDay) {
            x.displayProperty = `${x.label} (Maximum ${x.nbDay} jours)`;
          } else {
            x.displayProperty = `${x.label}`;
          }
          return x;
        });
      }),
    );
  }

  private checkValidityDatesLocal() {
    // Validation locale sans appel API - utilise les données du contrat déjà disponibles
    if (this.contract.startValidityDate && this.contract.endValidityDate) {
      const startDate = moment(this.contract.startValidityDate).startOf('day').toDate();
      const endDate = moment(this.contract.endValidityDate).startOf('day').toDate();
      if (endDate < startDate) {
        this.form.get('signatureDate').disable({ emitEvent: false });
      } else {
        if (!this.isUpdateDisabledPermission) {
          enableControlWhenAllowed(this.form, 'signatureDate', this.isUpdateDisabledPermission);
        }
      }
    }
  }

  private initValueChangesSubscription(): void {
    this.subscription.add(
      this.form.valueChanges
        .pipe(
          startWith(this.form.getRawValue()), // Émettre la valeur actuelle en premier
          pairwise(), // Compare [previous, current]
          debounceTime(500),
          filter(([prev, curr]) => {
            const prevStr = JSON.stringify(prev);
            const currStr = JSON.stringify(curr);
            const hasRealChanges = prevStr !== currStr;

            return hasRealChanges;
          }),
          map(([prev, curr]) => curr), // Ne garder que la valeur actuelle
        )
        .subscribe(value => {
          this.contractService.update(this.form.getRawValue()).subscribe(res => {
            console.log('✅ [valueChanges] UPDATE reçu du backend');
            this.contract = res.data;
          });
        }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contract && changes.contract.currentValue && this.form) {
      this.form.patchValue(
        {
          _id: this.contract._id,
          genericEngagement: this.contract.genericEngagement,
          object: this.contract.object,
          supplier: this.contract.supplier,
          supplierContractReference: this.contract.supplierContractReference,
          supplierContact: this.contract.supplierContact,
          validity: this.contract.validity,
          startValidityDate: this.contract.startValidityDate,
          endValidityDate: this.contract.endValidityDate,
          signatureDate: this.contract.signatureDate,
          renewalValidity: this.contract.renewalValidity,
          minNotice: this.contract.minNotice,
          paymentCondition: this.contract.paymentCondition,
          nbDay: this.contract.nbDay,
          renewalNb: this.contract.renewalNb,
          renewalDate: this.contract.renewalDate,
          noticePeriod: this.contract.noticePeriod || { displayedUnit: TimeUnit.DAY, quantity: 10080 },
          engagementContact: this.contract.engagementContact,
          supportEstablishment: null,
        },
        { emitEvent: false },
      );

      if (this.contract.startValidityDate) {
        enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);
      }
      if (this.contract.endValidityDate) {
        enableControlWhenAllowed(this.form, 'signatureDate', this.isUpdateDisabledPermission);
      }

      // Nettoie les subscriptions précédentes si elles sont fermées
      if (this.subscription.closed) {
        this.subscription = new Subscription();
      }

      if (this.contract.creationStep === 1) {
        this.initValueChangesSubscription();
      }
    }
    lockFormWhenReadOnly(this.form, this.isUpdateDisabledPermission);
  }

  onDateChanged(event: MatDatepickerInputEvent<Date>) {
    const startValidityDate = this.form.get('startValidityDate').value;
    if (event.value && startValidityDate) {
      const signatureDate = event.value;
      const validityDate = new Date(startValidityDate);

      if (signatureDate > validityDate) {
        this.form.get('signatureDate').setValue(validityDate, { emitEvent: false });
        this.snackBarService.danger('La date de signature ne peut être ultérieure à la date de début de contrat');
      }
    }
  }

  public addDenunciationDate(): void {
    const sub = this.dialog
      .open(ContractResumeCreateUpdateDeleteComponent, {
        data: { contract: this.contract },
      })
      .afterClosed()
      .subscribe(result => {});
    this.subscription.add(sub);
  }

  public openSupplierContactModal(): void {
    const contractGenericEngagementStatus =
      this.contract.genericEngagement?.format ?? this.form.controls.genericEngagement.value?.format;

    const sub = this.dialog
      .open(SupplierContactCreateUpdateDeleteComponent, {
        data: {
          id: this.form.controls.supplier.value._id,
          mode: CrudModeEnum.Create,
          isCreateInContract: true,
          defaults: {
            contactTypes: BaseContactType.filter(x => x.label === contractGenericEngagementStatus),
          },
        },
      })
      .afterClosed()
      .subscribe(async result => {
        if (result) {
          const contractGenericEngagementStatus =
            this.contract.genericEngagement?.format ?? this.form.controls.genericEngagement.value?.format;

          const contactTypeLabels = result.data.contactTypes.map((x: any) => x.label);
          const isEngagementStatusInContactTypes =
            contactTypeLabels.includes(contractGenericEngagementStatus) ||
            (!contractGenericEngagementStatus &&
              (contactTypeLabels.includes(ContactTypeEnum.CONTRACT) ||
                contactTypeLabels.includes(ContactTypeEnum.DEVIS)));

          if (isEngagementStatusInContactTypes) {
            this.supplierService
              .findAllSupplierContactById(null, null, null, null, null, {
                supplierId: this.form.controls.supplier.value._id,
              })
              .subscribe((supplier: any) => {
                this.form.controls.supplierContact.setValue(supplier.data.find(x => x._id === result.data._id));
              });
          }
        }
      });
    this.subscription.add(sub);
  }

  onSave() {
    // Marquer tous les champs comme "touched" pour afficher les erreurs
    Object.keys(this.form.controls).forEach(key => {
      this.form.controls[key].markAsTouched();
    });
    this.signatureCardTouched = true;

    // Vérifier si le formulaire est valide
    if (this.form.invalid) {
      this.snackBarService.danger('Veuillez remplir tous les champs requis');
      return;
    }

    // Vérifier si un fichier a été uploadé
    if (this.files.length === 0) {
      this.snackBarService.danger('Veuillez ajouter un document de contrat signé');
      return;
    }

    // Si tout est valide, procéder à l'enregistrement
    this.updateStep.emit(2);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
